import { Flex, Text } from "@chakra-ui/react";
import { isPopulatedArray, toNumberDisplayValue } from "@intentsify/utils";
import { defineChart } from "@tanstack/charts";
import { tooltip } from "@tanstack/charts/tooltip";
import { ErrorBoundary } from "components";
import { useCallback, useMemo } from "react";
import { byLook } from "theme/look";
import { Loader } from "../../Loader";
import { NoData } from "../../NoData";
import {
  BrandChart,
  ChartExportSvg,
  LineLegend,
  LineLegendItem,
  useBrandChartTheme,
} from "../BrandChart";
import { MIN_HEIGHT } from "../Charts.const";
import { LegacyStackedGroupedBarChart } from "./legacy/StackedGroupedBarChart";
import {
  buildStackedGroupedBarDefinition,
  createStackedGroupedBarColors,
  StackedGroupedBarRow,
} from "./StackedGroupedBarChart.definition";
import { StackedGroupedBarChartProps } from "./StackedGroupedBarChart.types";

export type { StackedGroupedBarChartProps };

const BrandStackedGroupedBarChart = ({
  isLoading,
  data,
  stretch = false,
  noDataMessage,
  valueAxisLabel,
  id,
  title,
  exportMode,
  minHeight = MIN_HEIGHT,
  legendPosition,
  legendConfig,
  maxYValue,
  legendTitle,
  hideGroupTicks = false,
  showTitle = false,
  hideBarsLabels = false,
}: StackedGroupedBarChartProps) => {
  const brandTheme = useBrandChartTheme();

  const buildDefinition = useCallback(
    () =>
      buildStackedGroupedBarDefinition(data, {
        brandTheme,
        valueAxisLabel,
        maxYValue,
        hideGroupTicks,
        hideBarsLabels,
      }),
    [
      data,
      brandTheme,
      valueAxisLabel,
      maxYValue,
      hideGroupTicks,
      hideBarsLabels,
    ],
  );

  const definition = useMemo(
    () => defineChart({ chart: buildDefinition, tooltip: { use: tooltip } }),
    [buildDefinition],
  );

  // Legend colours come from the same rules as the bars; the caller's
  // `legendConfig.range` is a legacy-palette value and is ignored here.
  const legend = useMemo((): LineLegendItem[] => {
    if (!legendConfig) {
      return [];
    }

    const { legendColorFor } = createStackedGroupedBarColors(
      data,
      brandTheme.mode,
    );

    return legendConfig.domain.map((label) => ({
      label,
      color: legendColorFor(label),
      swatch: true,
    }));
  }, [legendConfig, data, brandTheme.mode]);

  if (isLoading) {
    return <Loader minHeight={minHeight} stretch={stretch} />;
  }

  if (!isPopulatedArray(data)) {
    return (
      <NoData message={noDataMessage} height={minHeight} stretch={stretch} />
    );
  }

  const ariaLabel = title ?? valueAxisLabel ?? "Grouped bar chart";

  if (exportMode) {
    return (
      <ChartExportSvg
        id={id}
        title={title}
        ariaLabel={ariaLabel}
        brandTheme={brandTheme}
        buildDefinition={buildDefinition}
        legend={legend}
      />
    );
  }

  const legendOnRight = legendPosition === "right";

  return (
    <ErrorBoundary>
      <Flex direction="column" width="100%" height="100%" flexGrow={1}>
        {showTitle && title && (
          <Text fontSize="sm" fontWeight="semibold" mb={2}>
            {title}
          </Text>
        )}
        <Flex
          direction={legendOnRight ? "row" : "column"}
          gap={legendOnRight ? 4 : 0}
          flexGrow={stretch ? 1 : undefined}
        >
          <BrandChart
            definition={definition}
            brandTheme={brandTheme}
            ariaLabel={ariaLabel}
            minHeight={minHeight}
            height={stretch ? undefined : minHeight}
            flexGrow={stretch ? 1 : undefined}
            renderTooltipBody={({ points }) => {
              const row = points[0]?.datum as StackedGroupedBarRow | undefined;

              if (!row) {
                return null;
              }

              return (
                <Flex direction="column" fontSize="sm">
                  <Text>
                    <strong>{row.key}:</strong>{" "}
                    {toNumberDisplayValue(row.value)}
                  </Text>
                  {row.additionalTooltipContent && (
                    <Text>{row.additionalTooltipContent}</Text>
                  )}
                </Flex>
              );
            }}
          />
          {legend.length > 0 && (
            <LineLegend
              items={legend}
              title={legendTitle}
              direction={legendOnRight ? "column" : "row"}
            />
          )}
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};

const StackedGroupedBarChart = byLook(
  BrandStackedGroupedBarChart,
  LegacyStackedGroupedBarChart,
);

export { StackedGroupedBarChart };
